import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Tag,
  Space,
  Divider,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import {
  GET_SETTLEMENT_FEE_ITEM_PAGE,
  GET_SETTLEMENT_FEE_ITEM_MAX_CODE,
  GET_SETTLEMENT_FEE_ITEM_CHILDREN,
  CREATE_SETTLEMENT_FEE_ITEM,
  UPDATE_SETTLEMENT_FEE_ITEM,
} from '../../../graphql/queries/settlement/settlementFeeItemQueries';
import {
  GET_PRICING_FEE_ITEMS_BY_SCENARIO,
  GET_ALL_PRICING_FEE_ITEMS,
  usePricingScenarios,
  toPricingScenarioSelectOptions,
} from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';

interface SettlementFeeItemData {
  id: string;
  businessScenarioCode: number;
  name: string;
  nameEn?: string;
  code: number;
  isTaxInclude?: boolean;
  amountSign?: number;
  allowAdjustment?: boolean;
  pricingItemCodes: number[];
  mainItemId?: string | null;
  isAdjustItem?: boolean;
}

interface SettlementFeeItemPageResponse {
  content: SettlementFeeItemData[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface PricingFeeItem {
  id: string;
  businessFeeCode: number;
  businessFeeName: string;
  businessScenarioCode: number;
}

const amountSignOptions = [
  { value: 1, label: '正数（收入）' },
  { value: -1, label: '负数（支出）' },
];

const PAGE_SIZE = 20;

const SettlementFeeItemTab: React.FC = () => {
  const [pendingScenarioCode, setPendingScenarioCode] = useState<number | undefined>();
  const [pendingId, setPendingId] = useState<string>('');
  const [pendingCode, setPendingCode] = useState('');
  const [pendingName, setPendingName] = useState<string>('');

  const [appliedScenarioCode, setAppliedScenarioCode] = useState<number | undefined>();
  const [appliedId, setAppliedId] = useState<string>('');
  const [appliedCode, setAppliedCode] = useState('');
  const [appliedName, setAppliedName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SettlementFeeItemData | null>(null);
  const [pricingScenarioCode, setPricingScenarioCode] = useState<number | undefined>();
  const [form] = Form.useForm();

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (appliedId) f.id = Number(appliedId);
    if (appliedCode) f.code = Number(appliedCode);
    if (appliedName) f.nameLike = appliedName;
    if (appliedScenarioCode !== undefined) f.businessScenarioCode = appliedScenarioCode;
    return f;
  }, [appliedId, appliedCode, appliedName, appliedScenarioCode]);

  const { data: pageQueryData, loading, refetch } = useQuery<{
    getSettlementFeeItemPage: SettlementFeeItemPageResponse;
  }>(GET_SETTLEMENT_FEE_ITEM_PAGE, {
    variables: {
      filters: Object.keys(filters).length > 0 ? filters : null,
      pageRequest: { page: currentPage, size: PAGE_SIZE },
    },
    fetchPolicy: 'network-only',
  });

  const [loadPricingItems, { data: pricingItemsData }] = useLazyQuery<{
    getPricingFeeItemsByBusinessScenarioCode: PricingFeeItem[];
  }>(GET_PRICING_FEE_ITEMS_BY_SCENARIO, { fetchPolicy: 'cache-and-network' });

  const { data: allPricingItemsData } = useQuery<{
    getAllPricingFeeItems: PricingFeeItem[];
  }>(GET_ALL_PRICING_FEE_ITEMS);

  const [loadMaxCode] = useLazyQuery<{
    getSettlementFeeItemMaxCode: number;
  }>(GET_SETTLEMENT_FEE_ITEM_MAX_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const maxCode = Number(data?.getSettlementFeeItemMaxCode ?? 0);
      if (maxCode > 0) {
        form.setFieldValue('code', maxCode + 1);
        if (form.getFieldValue('allowAdjustment')) {
          form.setFieldsValue({ adjustIncrCode: maxCode + 2, adjustDecrCode: maxCode + 3 });
        }
      }
    },
  });

  const [createFeeItem, { loading: creating }] = useMutation(CREATE_SETTLEMENT_FEE_ITEM);
  const [updateFeeItem, { loading: updating }] = useMutation(UPDATE_SETTLEMENT_FEE_ITEM);

  const { scenarios } = usePricingScenarios();
  const scenarioOptions = toPricingScenarioSelectOptions(scenarios);
  const pricingScenarioOptions = scenarios.map(s => ({ value: Number(s.businessScenarioCode), label: s.businessScenarioName }));
  const pricingItems = pricingItemsData?.getPricingFeeItemsByBusinessScenarioCode ?? [];
  const allPricingItems = allPricingItemsData?.getAllPricingFeeItems ?? [];

  const scenarioMap = new Map(scenarios.map(s => [s.businessScenarioCode, s.businessScenarioName]));

  const pageData = pageQueryData?.getSettlementFeeItemPage;

  const [childrenMap, setChildrenMap] = useState<Record<string, SettlementFeeItemData[]>>({});
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});

  const [fetchChildren] = useLazyQuery<{
    getSettlementFeeItemChildren: SettlementFeeItemData[];
  }>(GET_SETTLEMENT_FEE_ITEM_CHILDREN, { fetchPolicy: 'network-only' });

  const handleExpand = async (expanded: boolean, record: SettlementFeeItemData) => {
    if (!expanded || childrenMap[record.id] !== undefined) return;
    setLoadingRows(prev => ({ ...prev, [record.id]: true }));
    try {
      const result = await fetchChildren({ variables: { mainItemId: Number(record.id) } });
      setChildrenMap(prev => ({ ...prev, [record.id]: result.data?.getSettlementFeeItemChildren ?? [] }));
    } finally {
      setLoadingRows(prev => ({ ...prev, [record.id]: false }));
    }
  };

  const handleSearch = () => {
    setAppliedScenarioCode(pendingScenarioCode);
    setAppliedId(pendingId);
    setAppliedCode(pendingCode);
    setAppliedName(pendingName);
    setCurrentPage(1);
    setChildrenMap({});
    setExpandedRowKeys([]);
  };

  const handleReset = () => {
    setPendingScenarioCode(undefined);
    setPendingId('');
    setPendingCode('');
    setPendingName('');
    setAppliedScenarioCode(undefined);
    setAppliedId('');
    setAppliedCode('');
    setAppliedName('');
    setCurrentPage(1);
    setChildrenMap({});
    setExpandedRowKeys([]);
  };

  const getChildColumns = (): ColumnsType<SettlementFeeItemData> => [
    { title: 'ID', dataIndex: 'id', width: 115 },
    { title: '调账费用项编码', dataIndex: 'code', width: 130 },
    { title: '调账费用项名称', dataIndex: 'name', width: 150 },
    { title: '英文名称', dataIndex: 'nameEn', width: 150, render: v => v ?? '-' },
    {
      title: '金额符号',
      dataIndex: 'amountSign',
      width: 90,
      render: v =>
        v === 1 ? <Tag color="green">正数</Tag> : <Tag color="red">负数</Tag>,
    },
    {
      title: '操作',
      width: 80,
      render: (_, item) => (
        <Button type="link" size="small" onClick={() => handleEdit(item)}>
          编辑
        </Button>
      ),
    },
  ];

  const handleCreate = () => {
    setEditingItem(null);
    setPricingScenarioCode(undefined);
    form.resetFields();
    form.setFieldsValue({ amountSign: 1, allowAdjustment: false, pricingItemCodes: [] });
    setModalOpen(true);
  };

  const handleEdit = async (item: SettlementFeeItemData) => {
    setEditingItem(item);
    const firstCode = item.pricingItemCodes?.[0];
    const matchedPricingItem = firstCode !== undefined
      ? allPricingItems.find(p => Number(p.businessFeeCode) === Number(firstCode))
      : undefined;
    const defaultPricingScenario = matchedPricingItem ? Number(matchedPricingItem.businessScenarioCode) : undefined;
    setPricingScenarioCode(defaultPricingScenario);
    form.resetFields();
    const formValues: Record<string, any> = { ...item };

    if (!item.isAdjustItem && item.allowAdjustment) {
      const result = await fetchChildren({ variables: { mainItemId: Number(item.id) } });
      const freshChildren = result.data?.getSettlementFeeItemChildren ?? [];
      setChildrenMap(prev => ({ ...prev, [item.id]: freshChildren }));

      const incrChild = freshChildren.find(c => c.amountSign === 1);
      const decrChild = freshChildren.find(c => c.amountSign === -1);
      if (incrChild) {
        formValues.adjustIncrCode = incrChild.code;
        formValues.adjustIncrName = incrChild.name;
        formValues.adjustIncrNameEn = incrChild.nameEn;
        formValues.adjustIncrAmountSign = incrChild.amountSign;
      }
      if (decrChild) {
        formValues.adjustDecrCode = decrChild.code;
        formValues.adjustDecrName = decrChild.name;
        formValues.adjustDecrNameEn = decrChild.nameEn;
        formValues.adjustDecrAmountSign = decrChild.amountSign;
      }
    }

    form.setFieldsValue(formValues);
    if (defaultPricingScenario !== undefined) {
      loadPricingItems({ variables: { businessScenarioCode: defaultPricingScenario } });
    }
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    try {
      if (editingItem) {
        const input: Record<string, any> = {
          id: editingItem.id,
          name: values.name,
          nameEn: values.nameEn ?? null,
          isTaxInclude: true,
          amountSign: values.amountSign,
          allowAdjustment: values.allowAdjustment ?? false,
          pricingItemCodes: values.pricingItemCodes ?? [],
        };

        if (!editingItem.isAdjustItem) {
          if (values.allowAdjustment) {
            input.adjustItems = [
              {
                name: values.adjustIncrName,
                nameEn: values.adjustIncrNameEn ?? null,
                code: values.adjustIncrCode,
                amountSign: values.adjustIncrAmountSign ?? 1,
              },
              {
                name: values.adjustDecrName,
                nameEn: values.adjustDecrNameEn ?? null,
                code: values.adjustDecrCode,
                amountSign: values.adjustDecrAmountSign ?? -1,
              },
            ];
          } else {
            input.adjustItems = [];
          }
        }

        await updateFeeItem({ variables: { input } });
        message.success('更新成功');
      } else {
        const adjustItems = values.allowAdjustment
          ? [
              {
                name: values.adjustIncrName,
                nameEn: values.adjustIncrNameEn ?? null,
                code: values.adjustIncrCode,
                amountSign: values.adjustIncrAmountSign ?? 1,
              },
              {
                name: values.adjustDecrName,
                nameEn: values.adjustDecrNameEn ?? null,
                code: values.adjustDecrCode,
                amountSign: values.adjustDecrAmountSign ?? -1,
              },
            ]
          : null;

        await createFeeItem({
          variables: {
            input: {
              businessScenarioCode: values.businessScenarioCode,
              name: values.name,
              nameEn: values.nameEn ?? null,
              code: values.code,
              amountSign: values.amountSign,
              allowAdjustment: values.allowAdjustment ?? true,
              pricingItemCodes: values.pricingItemCodes ?? [],
              adjustItems,
            },
          },
        });
        message.success('创建成功');
      }
      setModalOpen(false);
      setChildrenMap({});
      if (editingItem?.isAdjustItem && editingItem.mainItemId != null) {
        setExpandedRowKeys(prev => prev.filter(k => k !== String(editingItem.mainItemId)));
      }
      await refetch();
    } catch (error: any) {
      const errMsg = error?.graphQLErrors?.[0]?.message ?? error?.message ?? (editingItem ? '更新失败' : '创建失败');
      message.error(errMsg);
    }
  };

  const handleScenarioChange = (value: number) => {
    form.setFieldsValue({ code: undefined });
    loadMaxCode({ variables: { businessScenarioCode: value } });
  };

  const handlePricingScenarioChange = (value: number | undefined) => {
    const normalized = value !== undefined ? Number(value) : undefined;
    setPricingScenarioCode(normalized);
    form.setFieldsValue({ pricingItemCodes: [] });
    if (normalized !== undefined) {
      loadPricingItems({ variables: { businessScenarioCode: normalized } });
    }
  };

  const handleAllowAdjustmentChange = (checked: boolean) => {
    if (!checked) return;

    if (editingItem && !editingItem.isAdjustItem) {
      const children = childrenMap[editingItem.id] ?? [];
      if (children.length > 0) {
        const incrChild = children.find(c => c.amountSign === 1);
        const decrChild = children.find(c => c.amountSign === -1);
        if (incrChild) {
          form.setFieldsValue({
            adjustIncrCode: incrChild.code,
            adjustIncrName: incrChild.name,
            adjustIncrNameEn: incrChild.nameEn,
            adjustIncrAmountSign: incrChild.amountSign,
          });
        }
        if (decrChild) {
          form.setFieldsValue({
            adjustDecrCode: decrChild.code,
            adjustDecrName: decrChild.name,
            adjustDecrNameEn: decrChild.nameEn,
            adjustDecrAmountSign: decrChild.amountSign,
          });
        }
        return;
      }
    }

    const name = form.getFieldValue('name') ?? '';
    const nameEn = form.getFieldValue('nameEn') ?? '';
    const code = Number(form.getFieldValue('code'));
    form.setFieldsValue({
      adjustIncrName: name ? `${name}-调增` : '',
      adjustIncrNameEn: nameEn ? `${nameEn} Increase` : '',
      adjustIncrCode: code ? code + 1 : undefined,
      adjustIncrAmountSign: 1,
      adjustDecrName: name ? `${name}-调减` : '',
      adjustDecrNameEn: nameEn ? `${nameEn} Decrease` : '',
      adjustDecrCode: code ? code + 2 : undefined,
      adjustDecrAmountSign: -1,
    });
  };

  const hasExistingAdjustChildren =
    editingItem != null &&
    !editingItem.isAdjustItem &&
    (childrenMap[editingItem.id]?.length ?? 0) > 0;

  const columns: ColumnsType<SettlementFeeItemData> = [
    { title: 'ID', dataIndex: 'id', width: 115 },
    { title: '费用项编码', dataIndex: 'code', width: 130 },
    { title: '费用项名称', dataIndex: 'name', width: 200 },
    { title: '英文名称', dataIndex: 'nameEn', width: 200, render: v => v ?? '-' },
    {
      title: '业务场景',
      dataIndex: 'businessScenarioCode',
      width: 160,
      render: code => scenarioMap.get(code) ?? code,
    },
    {
      title: '金额符号',
      dataIndex: 'amountSign',
      width: 90,
      render: v =>
        v === 1 ? <Tag color="green">正数</Tag> : <Tag color="red">负数</Tag>,
    },
    {
      title: '允许调账',
      dataIndex: 'allowAdjustment',
      width: 90,
      render: v => (v ? '是' : '否'),
    },
    {
      title: '关联计费费用项',
      dataIndex: 'pricingItemCodes',
      render: (codes: number[]) =>
        codes.length === 0 ? '-' : codes.map(c => <Tag key={c}>{c}</Tag>),
    },
    {
      title: '操作',
      width: 80,
      render: (_, item) =>
        hasResource([ResourceCode.menu.SETTLEMENT_FEE_ITEM_MANAGER]) ? (
          <Button type="link" size="small" onClick={() => handleEdit(item)}>
            编辑
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title="结算费用项管理"
      extra={
        hasResource([ResourceCode.menu.SETTLEMENT_FEE_ITEM_MANAGER]) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            新增费用项
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="ID"
          allowClear
          style={{ width: 180 }}
          value={pendingId}
          onChange={e => setPendingId(e.target.value)}
          prefix={<SearchOutlined />}
        />
        <Input
          placeholder="费用项编码"
          allowClear
          style={{ width: 140 }}
          value={pendingCode}
          onChange={e => setPendingCode(e.target.value)}
        />
        <Input
          placeholder="费用项中文名称"
          allowClear
          style={{ width: 160 }}
          value={pendingName}
          onChange={e => setPendingName(e.target.value)}
        />
        <Select
          placeholder="按业务场景筛选"
          allowClear
          style={{ width: 200 }}
          value={pendingScenarioCode}
          onChange={setPendingScenarioCode}
          options={scenarioOptions}
        />
        <Button type="primary" onClick={handleSearch}>查询</Button>
        <Button onClick={handleReset}>重置</Button>
      </Space>

      <Table
        rowKey="id"
        dataSource={pageData?.content ?? []}
        columns={columns}
        loading={loading}
        scroll={{ x: 1000 }}
        expandable={{
          expandedRowKeys,
          rowExpandable: record => record.allowAdjustment === true && !record.isAdjustItem,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded
              ? [...expandedRowKeys, record.id]
              : expandedRowKeys.filter(k => k !== record.id)
            );
            handleExpand(expanded, record);
          },
          expandedRowRender: record => (
            <Table
              rowKey="id"
              size="small"
              dataSource={childrenMap[record.id] ?? []}
              loading={loadingRows[record.id] ?? false}
              columns={getChildColumns()}
              pagination={false}
              style={{ margin: '0 48px' }}
            />
          ),
        }}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: pageData?.totalElements ?? 0,
          onChange: page => { setCurrentPage(page); setChildrenMap({}); setExpandedRowKeys([]); },
          showSizeChanger: false,
          showTotal: total => `共 ${total} 条`,
        }}
      />

      <Modal
        title={editingItem ? '编辑结算费用项' : '新增结算费用项'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating || updating}
        okText="确认"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editingItem && (
            <>
              <Form.Item
                name="businessScenarioCode"
                label="业务场景"
                rules={[{ required: true, message: '请选择业务场景' }]}
              >
                <Select
                  placeholder="请选择业务场景"
                  options={scenarioOptions}
                  onChange={handleScenarioChange}
                />
              </Form.Item>
              <Form.Item
                name="code"
                label="费用项编码（全局唯一）"
                rules={[{ required: true, message: '请输入费用项编码' }]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="请输入编码（数字）" precision={0} />
              </Form.Item>
            </>
          )}
          {editingItem && (
            <Form.Item label="费用项编码">
              <InputNumber style={{ width: '100%' }} value={editingItem.code} disabled precision={0} />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label="费用项名称"
            rules={[{ required: true, message: '请输入费用项名称' }]}
          >
            <Input placeholder="请输入费用项名称" />
          </Form.Item>
          <Form.Item name="nameEn" label="英文名称">
            <Input placeholder="请输入英文名称（可选）" />
          </Form.Item>
          <Form.Item
            name="amountSign"
            label="金额符号"
            rules={[{ required: true, message: '请选择金额符号' }]}
          >
            <Select options={amountSignOptions} />
          </Form.Item>

          {(!editingItem || !editingItem.isAdjustItem) && (
            <Form.Item name="allowAdjustment" label="允许调账" valuePropName="checked">
              <Switch onChange={handleAllowAdjustmentChange} />
            </Form.Item>
          )}
          {(!editingItem || !editingItem.isAdjustItem) && (
            <>
              <Form.Item label="关联计费费用项业务场景">
                <Select
                  placeholder="请选择业务场景"
                  allowClear
                  options={pricingScenarioOptions}
                  value={pricingScenarioCode}
                  onChange={handlePricingScenarioChange}
                />
              </Form.Item>
              <Form.Item name="pricingItemCodes" label="关联计费费用项">
                <Select
                  mode="multiple"
                  placeholder={pricingScenarioCode ? '请选择关联计费费用项' : '请先选择业务场景'}
                  disabled={!pricingScenarioCode}
                  options={pricingItems.map(p => ({
                    value: p.businessFeeCode,
                    label: `${p.businessFeeName} (${p.businessFeeCode})`,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  showSearch
                />
              </Form.Item>
            </>
          )}
          {(!editingItem || !editingItem.isAdjustItem) && (
            <Form.Item shouldUpdate={(prev, cur) => prev.allowAdjustment !== cur.allowAdjustment}>
              {({ getFieldValue }) =>
                getFieldValue('allowAdjustment') ? (
                  <>
                    <Divider orientation="left" style={{ fontSize: 13 }}>调增子费用项</Divider>
                    <Form.Item
                      name="adjustIncrCode"
                      label="编码"
                      rules={[{ required: true, message: '请输入调增编码' }]}
                    >
                      <InputNumber style={{ width: '100%' }} precision={0} disabled={hasExistingAdjustChildren} />
                    </Form.Item>
                    <Form.Item
                      name="adjustIncrName"
                      label="中文名称"
                      rules={[{ required: true, message: '请输入调增名称' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="adjustIncrNameEn" label="英文名称">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="adjustIncrAmountSign"
                      label="金额符号"
                      rules={[{ required: true, message: '请选择金额符号' }]}
                    >
                      <Select options={amountSignOptions} />
                    </Form.Item>

                    <Divider orientation="left" style={{ fontSize: 13 }}>调减子费用项</Divider>
                    <Form.Item
                      name="adjustDecrCode"
                      label="编码"
                      rules={[{ required: true, message: '请输入调减编码' }]}
                    >
                      <InputNumber style={{ width: '100%' }} precision={0} disabled={hasExistingAdjustChildren} />
                    </Form.Item>
                    <Form.Item
                      name="adjustDecrName"
                      label="中文名称"
                      rules={[{ required: true, message: '请输入调减名称' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="adjustDecrNameEn" label="英文名称">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="adjustDecrAmountSign"
                      label="金额符号"
                      rules={[{ required: true, message: '请选择金额符号' }]}
                    >
                      <Select options={amountSignOptions} />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default SettlementFeeItemTab;
